import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheck } from '@ng-icons/heroicons/outline';
import {
  UiCheckbox,
  UiDataTable,
  UiDataTableCell,
  UiEmptyState,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiPageHeader,
  UiResizableColumns,
  UiSkeletonGrid,
} from '../../../shared/ui';
import { LeadsApiService } from './leads-api.service';
import type { LeadListItem } from './leads.types';

@Component({
  selector: 'app-leads-lista-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterLink,
    NgIcon,
    UiCheckbox,
    UiDataTable,
    UiDataTableCell,
    UiEmptyState,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiPageHeader,
    UiResizableColumns,
    UiSkeletonGrid,
  ],
  providers: [provideIcons({ heroCheck })],
  template: `
    <section class="p-6 space-y-4">
      <app-ui-page-header
        title="Leads"
        subtitle="Contatos com vendas no Revenue."
        density="compact"
      />

      <div class="flex items-center gap-2 text-sm text-ink">
        <button
          appCheckbox
          type="button"
          [ngpCheckboxChecked]="activeOnly"
          (ngpCheckboxCheckedChange)="setActiveOnly($event)"
          aria-label="Filtrar apenas leads com venda ativa"
        >
          <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
        </button>
        <span>Apenas com venda ativa</span>
      </div>

      <div appUiDataTableShell class="overflow-x-auto">
        <table appUiDataTable appUiResizableColumns key="revenue-leads">
          <thead appUiDataTableHead>
            <tr>
              <th appUiDataTableHeaderCell>Nome</th>
              <th appUiDataTableHeaderCell class="text-right"># Vendas</th>
              <th appUiDataTableHeaderCell class="text-right">Receita total</th>
              <th appUiDataTableHeaderCell>Última venda</th>
              <th appUiDataTableHeaderCell>Último status</th>
            </tr>
          </thead>
          <tbody>
            @for (lead of leads(); track lead.contact.id) {
              <tr appUiDataTableRow>
                <td appUiDataTableCell>
                  <a [routerLink]="['/crm/contatos']" [queryParams]="{ open: lead.contact.id }" class="text-accent-strong hover:underline">
                    {{ leadName(lead) }}
                  </a>
                </td>
                <td appUiDataTableCell class="text-right">{{ lead.aggregates.salesCount }}</td>
                <td appUiDataTableCell class="text-right font-mono">{{ formatTotal(lead.aggregates.totalRevenue) }}</td>
                <td appUiDataTableCell class="text-muted">{{ formatDate(lead.aggregates.latestSaleAt) }}</td>
                <td appUiDataTableCell class="text-ink">{{ lead.aggregates.latestStatus ?? '—' }}</td>
              </tr>
            }
            @if (!loading() && leads().length === 0) {
              <tr>
                <td appUiDataTableEmptyCell colspan="5">
                  <app-ui-empty-state
                    compact
                    title="Nenhum lead encontrado"
                    description="Ajuste os filtros ou aguarde a chegada de novas vendas."
                  />
                </td>
              </tr>
            }
            @if (loading()) {
              <tr>
                <td appUiDataTableEmptyCell colspan="5" aria-busy="true" aria-label="Carregando leads">
                  <app-ui-skeleton-grid [rows]="3" [rowHeight]="42" />
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  `,
})
export class LeadsListaPage {
  private readonly api = inject(LeadsApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly leads = signal<LeadListItem[]>([]);
  activeOnly = false;

  constructor() {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.api.listLeads({ has_active_sale: this.activeOnly })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.leads.set(resp.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  setActiveOnly(checked: boolean): void {
    this.activeOnly = checked;
    this.reload();
  }

  leadName(lead: LeadListItem): string {
    const c = lead.contact;
    const first = c.firstName ?? '';
    const last = c.lastName ?? '';
    const full = `${first} ${last}`.trim();
    return full || c.id.slice(0, 8);
  }

  formatTotal(total: string): string {
    const value = Number(total);
    if (!Number.isFinite(value)) return total;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso));
    } catch {
      return iso;
    }
  }
}
